import * as api from './returnReasonsApi';
import {
    Checkbox, FormControl, FormControlLabel, MenuItem, Select, TextField, Typography
} from '@mui/material';
import { ConfirmationModal, ModalProvider, useModal } from '../generalcomponents/ModalProvider';
import { DetailViewShell, FormRow, sx13 } from '../common/detailView';
import type { Location, ReturnReason } from './returnReasonsApi';
import { useCallback, useEffect, useState } from 'react';
import { useDetailView } from '../common/useDetailView';
import { useTranslation } from 'react-i18next';

interface Props {
    returnReasonId: number | null;
}

export function ReturnReasonView(props: Props) {
    return (
        <ModalProvider>
            <ReturnReasonViewInner {...props} />
        </ModalProvider>
    );
}

function ReturnReasonViewInner({ returnReasonId }: Props) {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const detail = useDetailView<ReturnReason>();

    const [locations, setLocations] = useState<Location[]>([]);

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const locs = await api.getLocations();
            setLocations([{ id: 0, locationName: '---' }, ...locs]);
            if (returnReasonId === null) {
                detail.setItem({ id: 0, code: '', name: '', adjustStock: false, stockLocationId: null });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getReturnReason(returnReasonId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [returnReasonId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const isCodeValid = (): boolean => !!item && !!item.code && item.code.length <= 20;

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = detail.isNew ?
                await api.createReturnReason(item) :
                await api.updateReturnReason(item);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = () => {
        if (!item) return;
        openModal(
            <ConfirmationModal
                header={t('common.delete')}
                message={t('returnreason.confirmDelete')}
                onModalResponse={(confirmed) => { if (confirmed) performDelete(); }}
            />
        );
    };

    const performDelete = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            await api.deleteReturnReason(item.id);
            window.history.back();
        });
    };

    const handleAdjustStockChange = (checked: boolean) => {
        if (!item) return;
        const next = { ...item, adjustStock: checked };
        if (!checked) next.stockLocationId = null;
        detail.setItem(next);
    };

    return (
        <DetailViewShell<ReturnReason>
            featureKey="devFeature_reactReturnReasonView"
            title={item ? `${item.code} ${item.name}` : ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isCodeValid()}
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(rr) => (<>
                <FormRow label={t('returnreason.code')}>
                    {!detail.isNew && !detail.isEditing ? (
                        <Typography sx={sx13}>{rr.code}</Typography>
                    ) : (
                        <TextField size="small" fullWidth
                            inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                            value={rr.code}
                            onChange={(e) => detail.update('code', e.target.value.toUpperCase())}
                            error={!isCodeValid()} />
                    )}
                </FormRow>

                <FormRow label={t('returnreason.name')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{rr.name}</Typography>
                    ) : (
                        <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                            value={rr.name} onChange={(e) => detail.update('name', e.target.value)} />
                    )}
                </FormRow>

                <FormRow label={t('returnreason.adjustStock')}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rr.adjustStock}
                                disabled={!detail.isEditing}
                                onChange={(e) => handleAdjustStockChange(e.target.checked)}
                            />
                        }
                        label=""
                    />
                </FormRow>

                {rr.adjustStock && (
                    <FormRow label={t('returnreason.location')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>
                                {locations.find((l) => l.id === rr.stockLocationId)?.locationName ?? ''}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={rr.stockLocationId ?? 0}
                                    onChange={(e) => detail.update('stockLocationId', e.target.value === 0 ? null : Number(e.target.value))}
                                >
                                    {locations.map((l) => (
                                        <MenuItem key={l.id} value={l.id}>{l.locationName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>
                )}
            </>)}
        </DetailViewShell>
    );
}
